import React, { useState } from 'react';
import { 
  Typography, 
  Card, 
  Steps, 
  Button, 
  Table, 
  Tag, 
  Space, 
  Statistic, 
  Row, 
  Col,
  Tabs,
  Breadcrumb
} from 'antd';
import { 
  UserOutlined, 
  ShoppingCartOutlined, 
  TeamOutlined, 
  CheckCircleOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

interface EmployeeData {
  key: string;
  name: string;
  phone: string;
  customers: number;
  orders: number;
  commission: number;
  status: 'active' | 'inactive';
}

interface CustomerData {
  key: string;
  name: string;
  phone: string;
  source: string;
  status: 'new' | 'confirmed' | 'ordered' | 'completed';
  orderAmount: number;
  createdAt: string;
  employeeName: string;
}

const MarketingFlow: React.FC = () => {
  const [activeTab, setActiveTab] = useState('1');

  // 员工数据
  const employeeData: EmployeeData[] = [
    {
      key: '1',
      name: '张三',
      phone: '13800138000',
      customers: 12,
      orders: 5,
      commission: 1250.5,
      status: 'active',
    },
    {
      key: '2',
      name: '李四',
      phone: '13900139000',
      customers: 8,
      orders: 3,
      commission: 850.0,
      status: 'active',
    },
    {
      key: '3',
      name: '王五',
      phone: '13700137000',
      customers: 15,
      orders: 7,
      commission: 1800.25,
      status: 'active',
    },
  ];

  // 客户数据
  const customerData: CustomerData[] = [
    {
      key: '1',
      name: '客户A',
      phone: '13600136000',
      source: '员工推广',
      status: 'ordered',
      orderAmount: 2000,
      createdAt: '2023-05-12',
      employeeName: '张三',
    },
    {
      key: '2',
      name: '客户B',
      phone: '13500135000',
      source: '扫码获客',
      status: 'completed',
      orderAmount: 3500,
      createdAt: '2023-05-15',
      employeeName: '李四',
    },
    {
      key: '3',
      name: '客户C',
      phone: '13400134000',
      source: '员工推广',
      status: 'new',
      orderAmount: 0,
      createdAt: '2023-05-18',
      employeeName: '王五',
    },
  ];

  // 员工表格列
  const employeeColumns: ColumnsType<EmployeeData> = [
    {
      title: '员工姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '推广客户数',
      dataIndex: 'customers',
      key: 'customers',
    },
    {
      title: '成交订单数',
      dataIndex: 'orders',
      key: 'orders',
    },
    {
      title: '累计佣金(元)',
      dataIndex: 'commission',
      key: 'commission',
      render: (commission) => `￥${commission.toFixed(2)}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {status === 'active' ? '在职' : '离职'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" size="small">查看详情</Button>
          <Button type="link" size="small">佣金明细</Button>
        </Space>
      ),
    },
  ];

  // 客户表格列
  const customerColumns: ColumnsType<CustomerData> = [
    {
      title: '客户姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        let text = '新客户';
        
        if (status === 'confirmed') {
          color = 'processing';
          text = '已确认';
        } else if (status === 'ordered') {
          color = 'warning';
          text = '已下单';
        } else if (status === 'completed') {
          color = 'success';
          text = '已完成';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '订单金额(元)',
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      render: (amount) => `￥${amount.toFixed(2)}`,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: '所属员工',
      dataIndex: 'employeeName',
      key: 'employeeName',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" size="small">详情</Button>
          <Button type="link" size="small">跟进</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb 
        style={{ marginBottom: 16 }}
        items={[
          { title: '营销管理' },
          { title: '营销流程' }
        ]} 
      />

      <Card style={{ marginBottom: 16 }}>
        <Title level={4}>员工营销业务流程</Title>
        <Steps
          current={-1}
          items={[
            {
              title: '员工推广',
              description: '员工进行推广获客',
              icon: <TeamOutlined />,
            },
            {
              title: '专属客服',
              description: '确定专属客服',
              icon: <UserOutlined />,
            },
            {
              title: '预约购物',
              description: '引导预约企业购物',
              icon: <ShoppingCartOutlined />,
            },
            {
              title: '客户下单',
              description: '客户确认并下单',
              icon: <CheckCircleOutlined />,
            },
            {
              title: '佣金结算',
              description: '计算并分配佣金',
              icon: <DollarOutlined />,
            },
            {
              title: '财务审核',
              description: '财务审核后结算',
              icon: <FileTextOutlined />,
            },
          ]}
        />
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="员工总数" 
              value={employeeData.length} 
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="客户总数" 
              value={customerData.length} 
              prefix={<UserOutlined />}
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="订单总数" 
              value={customerData.filter(item => item.status === 'ordered' || item.status === 'completed').length} 
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="佣金总额(元)" 
              value={employeeData.reduce((acc, curr) => acc + curr.commission, 0)} 
              precision={2}
              prefix="￥"
              valueStyle={{ color: '#529C8A' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="员工管理" key="1">
            <Table 
              columns={employeeColumns} 
              dataSource={employeeData} 
              pagination={false}
            />
          </TabPane>
          <TabPane tab="客户管理" key="2">
            <Table 
              columns={customerColumns} 
              dataSource={customerData} 
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default MarketingFlow; 